import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, RotateCcw, LogOut, ArrowRight, Loader2 } from "lucide-react";

// Shown on every screen while inside the no-signup demo sandbox. Lets a visitor
// switch between the manager and any cleaner, reset the data, or exit (which
// deletes the throwaway tenant).
export function DemoBanner() {
  const { user, motel, switchDemo, resetDemo, endDemo } = useAuth();
  const navigate = useNavigate();
  const [cleaners, setCleaners] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!motel?.is_demo) return;
    api.get("/demo/cleaners").then(({ data }) => setCleaners(data.cleaners || [])).catch(() => {});
  }, [motel?.is_demo]);

  if (!motel?.is_demo) return null;

  const currentValue = user?.role === "cleaner" ? `cleaner:${user.id}` : "manager";

  const onSwitch = async (val) => {
    setBusy(true);
    try {
      if (val === "manager") {
        await switchDemo("manager");
        navigate("/manager");
      } else {
        const id = val.split(":")[1];
        await switchDemo("cleaner", id);
        navigate("/clean");
      }
    } catch {
      toast.error("Could not switch role");
    } finally {
      setBusy(false);
    }
  };

  const onReset = async () => {
    setBusy(true);
    try {
      await resetDemo();
      toast.success("Demo reset to fresh data");
      navigate("/manager");
    } catch {
      toast.error("Could not reset the demo");
    } finally {
      setBusy(false);
    }
  };

  const onExit = async () => {
    setBusy(true);
    await endDemo();
    navigate("/");
  };

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2 bg-indigo-950 px-4 py-2 text-xs text-indigo-50"
      data-testid="demo-banner"
    >
      <div className="flex items-center gap-2">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-indigo-300" />}
        <span className="font-semibold">Demo mode</span>
        <span className="hidden text-indigo-300 sm:inline">— explore freely; everything resets when you leave.</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-indigo-300">View as</span>
        <Select value={currentValue} onValueChange={onSwitch} disabled={busy}>
          <SelectTrigger className="h-7 w-[168px] border-indigo-700 bg-indigo-900 text-indigo-50" data-testid="demo-role-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="manager">Manager (owner)</SelectItem>
            {cleaners.map((c) => (
              <SelectItem key={c.id} value={`cleaner:${c.id}`}>Cleaner · {c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" variant="ghost" className="h-7 gap-1 text-indigo-50 hover:bg-indigo-900 hover:text-white" onClick={onReset} disabled={busy} data-testid="demo-reset-btn">
          <RotateCcw className="h-3.5 w-3.5" /> Reset
        </Button>
        <Button size="sm" className="h-7 gap-1 bg-white text-indigo-950 hover:bg-indigo-100" onClick={() => {
          try {
            sessionStorage.setItem("klena_trial_prefill", JSON.stringify({
              motel_name: (motel?.name || "").replace(/\s*\(demo\)\s*$/i, ""),
              location: motel?.location || "",
            }));
          } catch { /* ignore */ }
          navigate("/signup");
        }} data-testid="demo-trial-btn">
          Start free trial <ArrowRight className="h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="ghost" className="h-7 gap-1 text-indigo-200 hover:bg-indigo-900 hover:text-white" onClick={onExit} disabled={busy} data-testid="demo-exit-btn">
          <LogOut className="h-3.5 w-3.5" /> Exit
        </Button>
      </div>
    </div>
  );
}
